"use client";

import Image from "next/image";
import { Icon, IconName } from "../icons/Icon";
import { spacing } from "~/utils/tailwindUtils";
import Badge from "../badges/Badge";
import Text from "../texts/Text";
import { twMerge } from "tailwind-merge";
import { memo, useState } from "react";

type ProductImageProps = {
  imageUrl?: string | undefined;
  width?: number;
  height?: number;
  iconSize?: number;
  className?: string;
  isOpen?: boolean;
  restProductsCount?: number;
};

/**
 * Renders a product image or a fallback icon if the image fails to load.
 * @param {ProductImageProps} props - Component props
 * @param {string} props.imageUrl - URL of the product image
 * @param {number} props.width - Width of the image
 * @param {number} props.height - Height of the image
 * @param {string} props.className - Additional CSS classes for the image
 * @param {boolean} props.isOpen - Whether the image is open
 * @param {number} props.restProductsCount - Number of additional products
 */
const ProductImage = memo(({
  imageUrl,
  width = spacing[8],
  height = spacing[8],
  iconSize,
  className = "",
  isOpen = false,
  restProductsCount = 0,
}: ProductImageProps) => {
  const decodedImageUrl = imageUrl && imageUrl !== "null" ? decodeURIComponent(imageUrl) : null;

  // Track retry state keyed by the original URL to reset when URL changes
  const [retryState, setRetryState] = useState<{
    originalUrl: string | null;
    currentUrl: string | null;
    hasTriedAlternate: boolean;
    isError: boolean;
  }>({
    originalUrl: decodedImageUrl,
    currentUrl: decodedImageUrl,
    hasTriedAlternate: false,
    isError: false,
  });

  // Reset state when the source URL changes
  const currentState =
    retryState.originalUrl === decodedImageUrl
      ? retryState
      : {
        originalUrl: decodedImageUrl,
        currentUrl: decodedImageUrl,
        hasTriedAlternate: false,
        isError: false,
      };

  const handleImageError = () => {
    if (!currentState.hasTriedAlternate && currentState.currentUrl) {
      // Try alternate extension: jpg <-> png
      let alternateUrl: string | null = null;
      const url = currentState.currentUrl.toLowerCase();
      if (url.endsWith(".jpg")) {
        alternateUrl = currentState.currentUrl.slice(0, -4) + ".png";
      } else if (url.endsWith(".jpeg")) {
        alternateUrl = currentState.currentUrl.slice(0, -5) + ".png";
      } else if (url.endsWith(".png")) {
        alternateUrl = currentState.currentUrl.slice(0, -4) + ".jpg";
      }

      if (alternateUrl) {
        setRetryState({
          ...currentState,
          currentUrl: alternateUrl,
          hasTriedAlternate: true,
        });
        return;
      }
    }
    // No alternate to try or alternate also failed
    setRetryState({ ...currentState, isError: true });
  };

  const productImageOrFallbackIcon =
    !currentState.isError && currentState.currentUrl ? (
      <Image
        src={currentState.currentUrl}
        alt="product"
        width={width}
        height={height}
        className={className}
        unoptimized // Skip Next.js image proxy to avoid 403 from S3 buckets
        onError={handleImageError}
      />
    ) : (
      <Icon
        name={IconName.FallbackImage}
        width={iconSize || width}
        height={iconSize || height}
        className={className}
        data-testid="product-image-fallback"
      />
    );

  return (
    <div className="relative">
      {productImageOrFallbackIcon}

      {restProductsCount > 0 && (
        <Badge
          className={twMerge(
            "absolute top-0 right-0 transform translate-x-1/2 translate-y-[-50%] bg-white border border-primary rounded-md p-0 transition-all ease-in-out md:px-2 md:py-0.5",
            !isOpen ? "duration-1500 opacity-100" : "duration-750 opacity-0"
          )}
        >
          <Text variant="span" fontWeight="semibold" className="leading-3.5">
            +{restProductsCount}
          </Text>
        </Badge>
      )}
    </div>
  );
});

ProductImage.displayName = "ProductImage";

export default ProductImage;
